import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Compass, LayoutGrid, BookOpenCheck, Sparkles, LogOut, ListTodo, Settings, Bell, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import FaviconBadge from "@/components/FaviconBadge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const initialsFrom = (name = "") =>
  name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const NAV = [
  { to: "/app/leads", label: "Leads", icon: LayoutGrid, testid: "nav-leads" },
  { to: "/app/tasks", label: "Tareas", icon: ListTodo, testid: "nav-tasks", badge: true },
  { to: "/app/reminders", label: "Recordatorios", icon: Bell, testid: "nav-reminders" },
  { to: "/app/best-practices", label: "Reglas & Automatizaciones", icon: BookOpenCheck, testid: "nav-best-practices" },
  { to: "/app/supervision", label: "Resumen", icon: BarChart3, testid: "nav-supervision", adminOnly: true },
  { to: "/app/settings", label: "Ajustes", icon: Settings, testid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const tasksSummary = useQuery({
    queryKey: ["tasks-summary"],
    queryFn: async () => (await api.get("/zicasso/tasks/summary")).data,
    refetchInterval: 60_000,
  });
  const pendingCount = tasksSummary.data?.pending || 0;

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <FaviconBadge />
      {/* Sidebar */}
      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col justify-between bg-[#1E2320] px-4 py-6 text-white md:flex"
        data-testid="app-sidebar"
      >
        <div>
          <div className="mb-8 flex items-center justify-between gap-2 px-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#C45A45] shadow-lg">
                <Compass className="h-5 w-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-heading text-lg font-semibold tracking-tight">Zicasso</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Leads Manager</div>
              </div>
            </div>
            <NotificationsBell />
          </div>

          <nav className="space-y-1" data-testid="app-nav">
            {NAV.filter((n) => !n.adminOnly || user?.is_admin).map(({ to, label, icon: Icon, testid, badge }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{label}</span>
                {badge && pendingCount > 0 && (
                  <span
                    className="ml-auto rounded-full bg-[#C45A45] px-1.5 py-0.5 text-[9px] font-bold text-white"
                    data-testid="nav-tasks-badge"
                  >
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            ))}

            <div
              className="mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/25"
              title="Disponible en la fase 2"
              data-testid="nav-ai-soon"
            >
              <Sparkles className="h-4 w-4 shrink-0" />
              <span className="truncate">Asistente IA</span>
              <span className="ml-auto rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white/50">
                Pronto
              </span>
            </div>
          </nav>
        </div>

        {/* User */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/5"
              data-testid="user-menu-trigger"
            >
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ background: user?.avatar_color || "#C45A45" }}
              >
                {initialsFrom(user?.full_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{user?.full_name}</div>
                <div className="truncate text-[11px] text-white/40">
                  {user?.is_admin ? "Admin" : "Especialista"}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52" data-testid="user-menu-content">
            <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="logout-button" className="text-[#C45A45]">
              <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      {/* Main */}
      <main className="flex-1 md:pl-60">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between border-b bg-[#1E2320] px-4 py-3 text-white md:hidden">
          <div className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-[#C45A45]" />
            <span className="font-heading text-base font-semibold">Zicasso</span>
          </div>
          <button onClick={onLogout} data-testid="logout-button-mobile" className="text-white/70">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
